import { produce } from 'immer';
import {
  applyDefaultViewName,
  applyTimeRangeToSpec,
  DashboardResource,
  ensureSavedViewsDefinition,
  SavedView,
  savedViewsToSavedViewsDefinition,
} from '@/core';
import { useTimeRange } from '@/plugin-system';
import { useEventStoreContext } from './Events';
import { useSavedViews, useTemplateVariableActions, useTemplateVariableViewState } from './TemplateVariableProvider';
import { useDashboard } from './useDashboard';

export interface UpdateDashboardDefaultsOptions {
  /** When true, persist the current active time range to the spec. */
  updateTimeRange?: boolean;
  /** When true, snapshot current variable values as the new defaults. */
  updateVariableDefaults?: boolean;
  /** Set, clear (null), or leave unchanged (undefined) the default view name. */
  defaultViewName?: string | null;
  /** Create a new saved view from the current variable values. */
  createSavedView?: { name: string };
  /** When true, persist event definitions to the spec. */
  updateEvents?: boolean;
}

/**
 * Applies default value updates (time range, variables, saved views, events)
 * to the dashboard spec. Returns the updated DashboardResource.
 */
export function useUpdateDashboardDefaults() {
  const { dashboard, setDashboard } = useDashboard();
  const { setVariableDefaultValues } = useTemplateVariableActions();
  const { currentView } = useTemplateVariableViewState();
  const savedViews = useSavedViews();
  const { timeRange } = useTimeRange();
  const { eventDefinitions } = useEventStoreContext();

  const updateDashboardDefaults = (options: UpdateDashboardDefaultsOptions): DashboardResource => {
    const updatedDashboard = produce(dashboard, (draft) => {
      if (options.updateTimeRange) {
        applyTimeRangeToSpec(draft.spec, timeRange);
      }

      // Snapshots current variable runtime values as the new spec defaults
      if (options.updateVariableDefaults) {
        draft.spec.variables = setVariableDefaultValues();
      }

      if (options.defaultViewName !== undefined) {
        draft.spec.saved_views = ensureSavedViewsDefinition(draft.spec.saved_views);
        applyDefaultViewName(draft.spec.saved_views, options.defaultViewName);
      }

      if (options.createSavedView) {
        const newView: SavedView = {
          name: options.createSavedView.name,
          variable_values: currentView,
        };
        const updatedViews = [...savedViews, newView];
        const existingDefaultViewName = draft.spec.saved_views?.spec.default_view_name;
        draft.spec.saved_views = savedViewsToSavedViewsDefinition(updatedViews, existingDefaultViewName);
      }

      if (options.updateEvents) {
        draft.spec.events = eventDefinitions;
      }
    });

    setDashboard(updatedDashboard);
    return updatedDashboard;
  };

  return { updateDashboardDefaults };
}
